import React from 'react';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';

/** Share of the bar, at each end, over which a readout anchors to the band's outer edge. */
const EDGE_PERCENT = 15;

/** Above the band, outside the bar's own overflow clip and out of the pointer's way. */
export const BandTooltipSlot = styled.div`
  position: absolute;
  bottom: calc(100% + 6px);
  z-index: 2;
  white-space: nowrap;
  pointer-events: none;
`;

/**
 * Centred over its band, except near either end of the bar, where it lines up with the band's outer
 * edge so the box never hangs past the bar into the card's padding.
 */
export const bandTooltipPosition = (
  startPercent: number,
  widthPercent: number
): React.CSSProperties => {
  const centre = startPercent + widthPercent / 2;

  if (centre < EDGE_PERCENT) {
    return { left: `${startPercent}%` };
  }

  if (centre > 100 - EDGE_PERCENT) {
    return { left: `${startPercent + widthPercent}%`, transform: 'translateX(-100%)' };
  }

  return { left: `${centre}%`, transform: 'translateX(-50%)' };
};

// The look of the Intelligence chart tooltips (DonutChart, TrendChart): caption over reading.
const ReadoutBox = styled.div`
  padding: ${tokens.space['4']} ${tokens.space['8']};
  border: 1px solid ${tokens.color['border-subtle']};
  border-radius: ${tokens.radius.sm};
  background: ${tokens.color['surface-card']};
  font-size: ${tokens.type['label-size']};
  line-height: ${tokens.type['label-line']};
`;

const ReadoutLabel = styled.div`
  color: ${tokens.color['text-muted']};
`;

const ReadoutValue = styled.div`
  color: ${tokens.color['text-primary']};
`;

export interface IBandReadoutProps {
  label: string;
  share: string;
}

/** A band's name and share, for a band whose caption does not fit on its fill. */
export const BandReadout: React.FC<IBandReadoutProps> = ({ label, share }) => (
  <ReadoutBox data-testid="segmented-distribution-bar-readout">
    <ReadoutLabel>{label}</ReadoutLabel>
    <ReadoutValue>{share}</ReadoutValue>
  </ReadoutBox>
);
